import React, { useMemo, useContext } from 'react'
import { Grid, GridCell, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import MetadataIcon from 'assets/icons/metadataGlyph24'
import ClockIcon from 'assets/icons/clock'
import LocationIcon from 'assets/icons/location'
import PrivacyIcon from 'assets/icons/privacy'
import { arrayToSentenceFragment } from 'src/components/utils'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import ThemeContext from 'src/libraries/contexts/Theme'

const EventDetailsBlock = ({ showLocation }) => {
   const theme = useContext(ThemeContext)
   const { initiative } = useContext(InitiativeHierarchyContext)

   const [publicPrivilegeStatement, communityMemberPrivilegeStatement] = useMemo(() => {
      // Little util to help us
      const getRolePolicy = (roleType, [roleOwnerFilterKey, roleOwnerFilterValue]) => (
         initiative.rolePolicies.find(policy => (
            policy.role.type === roleType &&
               policy.roleOwner[roleOwnerFilterKey] === roleOwnerFilterValue
         ))
      )

      // The various role policies we are concerned with
      const communityPrimaryRolePolicy = getRolePolicy('primary', ['__typename', 'Organization'])
      const publicRolePolicy = getRolePolicy('public', ['__typename', 'Organization'])

      // The privilege keys themselves
      const spks = policy => policy.privileges
         .map(p => p.spk)
         .filter(spk => ['join', 'view'].includes(spk))
         .map(spk => (spk === 'join' ? initiative.joinVerb : spk))

      const communityPrimaryRolePolicyPrivilegeSPKs = spks(communityPrimaryRolePolicy)
      const publicRolePolicyPrivilegeSPKs = spks(publicRolePolicy)

      const computedPublicPrivilegeStatement = publicRolePolicy.privileges.length
         ? `Any public user can ${arrayToSentenceFragment(publicRolePolicyPrivilegeSPKs)}
            this ${initiative.type.nameSingular}.`
         : `This ${initiative.type.nameSingular} is not visible to the public.`

      const computedCommunityMemberPrivilegeStatement = communityPrimaryRolePolicy.privileges.length
         ? `Any Community Member can
            ${arrayToSentenceFragment(communityPrimaryRolePolicyPrivilegeSPKs)}
            this ${initiative.type.nameSingular}.`
         : `Only Community Members that are invited can see and ${initiative.joinVerb} this
            ${initiative.type.nameSingular}.`

      return [
         computedPublicPrivilegeStatement,
         computedCommunityMemberPrivilegeStatement
      ]
   }, [initiative])

   return (
      <View
         display="flex"
         width="100%"
         padding="20px 0px">
         <View
            paddingTop="15px"
            borderTop="1px solid #eee"
            paddingBottom="15px"
            borderBottom="1px solid #eee">
            <Text
               color="#666"
               size={theme.tmpSubBodyTextSize}
               style={{ letterSpacing: '-0.1px' }}
               weight="normal">
               <Grid columns="1" spacing="15px">
                  {initiative.eventIsVirtual && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           padding="0px 0px 15px 0px"
                           borderBottom="1px solid #eee"
                           alignItems="center">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <MetadataIcon
                                 color="#888"
                                 width="16px"
                                 height="16px"
                              />
                           </View>
                           <View>
                              <Text size={theme.tmpSubBodyTextSize} color="#666">
                                 This is an online event only
                              </Text>
                           </View>
                        </View>
                     </GridCell>
                  )}
                  <GridCell>
                     <View
                        float="left"
                        width="100%"
                        display="flex"
                        padding="0px 0px 15px 0px"
                        borderBottom="1px solid #eee"
                        alignItems="center">
                        <View
                           float="left"
                           width="20px"
                           height="20px"
                           flex="0 0 auto"
                           display="flex"
                           justifyContent="center"
                           alignItems="center"
                           marginLeft="6px"
                           marginRight="24px">
                           <ClockIcon
                              color="#666"
                              width="16px"
                              height="16px"
                           />
                        </View>
                        <View>
                           <Text size={theme.tmpSubBodyTextSize} color="#666">
                              {initiative.startDateTime
                                 ? initiative.startDateTime
                                 : 'Date and time has not yet been set'
                              }
                           </Text>
                           {initiative.endDateTime && (
                              <Text size={theme.tmpSubBodyTextSize} color="#666">
                                 {`to ${initiative.endDateTime}`}
                              </Text>
                           )}
                        </View>
                     </View>
                  </GridCell>
                  {!initiative.eventIsVirtual &&
                  showLocation &&
                  initiative.location.formattedAddress && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           alignItems="center"
                           padding="0px 0px 15px 0px"
                           borderBottom="1px solid #eee">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <LocationIcon
                                 color="#666"
                                 width="18px"
                                 height="18px"
                              />
                           </View>
                           <View>
                              <Text size={theme.tmpSubBodyTextSize} color="#666">
                                 {initiative.location.formattedAddress}
                                 &nbsp;
                                 <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(initiative.location.formattedAddress)}`}>
                                    <Text size={theme.tmpSubBodyTextSize} color="#888">
                                       <u>View Map</u>
                                    </Text>
                                 </a>
                              </Text>
                           </View>
                        </View>
                     </GridCell>
                  )}
                  {/* This is for Sessions */}
                  {initiative.ancestors.length > 0 && initiative.location.name && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           alignItems="center">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <LocationIcon
                                 color="#666"
                                 width="18px"
                                 height="18px"
                              />
                           </View>
                           <View>
                              <Text size="2" color="#666">
                                 {initiative.location.name}
                              </Text>
                           </View>
                        </View>
                     </GridCell>
                  )}
                  {initiative.ancestors.length === 0 && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           alignItems="center">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <PrivacyIcon
                                 color="#666"
                                 width="18px"
                                 height="18px"
                              />
                           </View>
                           <View>
                              <Text size={theme.tmpSubBodyTextSize} color="#666">
                                 {publicPrivilegeStatement}
                                 <br />
                                 {communityMemberPrivilegeStatement}
                              </Text>
                           </View>
                        </View>
                     </GridCell>
                  )}
               </Grid>
            </Text>
         </View>
      </View>
   )
}

EventDetailsBlock.propTypes = {
   showLocation: PropTypes.bool
}

EventDetailsBlock.defaultProps = {
   showLocation: true
}

export default EventDetailsBlock
